import React, { useEffect, useState } from "react";
import {
  Application,
  getAppHistory,
  uploadApplication,
  deleteApplication,
} from "../api/admin";

import styles from "./CommonPage.module.css";
import { Upload, FileText, Monitor, Trash2 } from "lucide-react";

const AppsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"MANAGER" | "AGENT">("MANAGER");
  const [platform, setPlatform] = useState<"WINDOWS" | "ANDROID">("WINDOWS");
  const [history, setHistory] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);

  // Upload State
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [activeTab]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getAppHistory(activeTab, platform);
      setHistory(data);
    } catch (error) {
      console.error("Failed to fetch app history", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !version) {
      alert("파일과 버전을 모두 입력해주세요.");
      return;
    }

    setUploading(true);
    try {
      await uploadApplication(file, activeTab, platform, version);
      alert("업로드가 완료되었습니다.");
      setFile(null);
      setVersion("");
      fetchHistory();
    } catch (error) {
      console.error("Upload failed", error);
      alert("업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await deleteApplication(id);
      alert("삭제되었습니다.");
      fetchHistory();
    } catch (error) {
      console.error("Delete failed", error);
      alert("삭제에 실패했습니다.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.wrapper}>
          <h1>프로그램 관리</h1>
          <p>Manager 및 Agent용 프로그램을 관리합니다.</p>
        </div>
      </div>

      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          gap: "16px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "16px",
        }}
      >
        <button
          className={
            activeTab === "MANAGER" ? styles.primaryBtn : styles.secondaryBtn
          }
          onClick={() => setActiveTab("MANAGER")}
        >
          Manager (상담원)
        </button>
        <button
          className={
            activeTab === "AGENT" ? styles.primaryBtn : styles.secondaryBtn
          }
          onClick={() => setActiveTab("AGENT")}
        >
          Agent (고객)
        </button>
      </div>

      <div className={styles.appsGrid}>
        {/* Upload Section */}
        <div className={styles.card} style={{ height: "fit-content" }}>
          <h3
            style={{
              marginBottom: "16px",
              margin: "0 0 16px 0",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Upload size={20} /> 새 버전 업로드
          </h3>

          <div style={{ marginBottom: "20px" }}>
            {/* Platform selection removed - Windows only */}
          </div>

          <form
            onSubmit={handleUpload}
            className={styles.formCard}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
              border: "none",
              padding: 0,
              boxShadow: "none",
            }}
          >
            <div className={styles.formGroup}>
              <label htmlFor="versionInput">버전 (예: 1.0.0)</label>
              <input
                style={{ marginTop: "8px" }}
                id="versionInput"
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="버전을 입력하세요"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label style={{ marginBottom: "8px", display: "block" }}>
                파일 선택
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <label
                  htmlFor="fileInput"
                  className={styles.secondaryBtn}
                  style={{
                    cursor: "pointer",
                    margin: 0,
                    padding: "8px 12px",
                    fontSize: "0.9rem",
                  }}
                >
                  <FileText size={16} /> 파일 찾기
                </label>
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: file ? "#111827" : "#9ca3af",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file ? file.name : "선택된 파일 없음"}
                </span>
                <input
                  id="fileInput"
                  type="file"
                  onChange={handleFileChange}
                  accept=".exe"
                  style={{ display: "none" }}
                />
              </div>
            </div>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={uploading}
              style={{ justifyContent: "center", marginTop: "8px" }}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  업로드 중...
                </>
              ) : (
                <>
                  <Upload size={18} /> 업로드
                </>
              )}
            </button>
          </form>
        </div>

        {/* History Section */}
        <div className={styles.tableContainer}>
          <h3
            style={{
              padding: "24px 24px 16px 24px",
              margin: "0px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FileText size={20} />{" "}
            {activeTab === "MANAGER" ? "Manager" : "Agent"} / {platform} 버전
            기록
          </h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>버전</th>
                <th>파일명</th>
                <th>업로드 일시</th>
                <th style={{ width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{ textAlign: "center", padding: "24px" }}
                  >
                    로딩 중...
                  </td>
                </tr>
              ) : (
                history.map((app) => (
                  <tr key={app.id}>
                    <td data-label="버전" style={{ fontWeight: 600 }}>
                      {app.version}
                    </td>
                    <td
                      data-label="파일명"
                      style={{ fontSize: "0.9rem", color: "#4b5563" }}
                    >
                      {app.originalFileName}
                    </td>
                    <td
                      data-label="업로드 일시"
                      style={{ fontSize: "0.85rem", color: "#6b7280" }}
                    >
                      {new Date(app.createdAt).toLocaleString()}
                    </td>
                    <td data-label="관리">
                      <button
                        onClick={() => handleDelete(app.id)}
                        className={styles.deleteBtn}
                        title="삭제"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
              {history.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      color: "#6b7280",
                    }}
                  >
                    업로드 기록이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AppsPage;
